import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PnLFilters from "./PnLFilters";
import PnLDetailDrawer from "./PnLDetailDrawer";
import PnLRefreshModal from "./PnLRefreshModal";
import { PnLKpis, PnLLegend, PnLResults } from "./PnLSummaryParts";
import { useRefreshFlow, useSummaryData } from "./usePnlSummary";
import { usePnlEntityOptions } from "./usePnlEntityOptions";
import {
  applyFilterChange,
  buildCsv,
  buildCsvFilename,
  downloadCsv,
  filterRows,
  formatLastRefreshed,
  getCategoryOptions,
  getDefaultFilters,
  getTypeOptions,
  groupRowsByMonth,
  isPnlReportUser,
  pruneEntitySelections,
  pruneSubTypes,
  sumTotals,
  toggleKey,
  validateFilters,
} from "./pnlReportUtils";
import "./pnlReport.css";

const PnLSummaryReport = () => {
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state?.auth?.user);
  const canView = isPnlReportUser(userDetails?.Value);

  const [filters, setFilters] = useState(() => getDefaultFilters());
  const [filterError, setFilterError] = useState("");
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const [selectedRow, setSelectedRow] = useState(null);

  const { appliedFilters, rows, optionRows, loading, loadError, loadReport } = useSummaryData(canView);
  const { companyOptions, sellerOptions, warehouseOptions } = usePnlEntityOptions(
    canView,
    filters.companyIds,
    filters.sellerIds
  );

  // Selections that the narrowed lists no longer offer are dropped.
  useEffect(() => {
    setFilters((previous) => pruneEntitySelections(previous, sellerOptions, warehouseOptions));
  }, [sellerOptions, warehouseOptions]);

  const visibleRows = useMemo(
    () => filterRows(rows, appliedFilters.categories, appliedFilters.subTypes),
    [rows, appliedFilters.categories, appliedFilters.subTypes]
  );
  const groups = useMemo(() => groupRowsByMonth(visibleRows), [visibleRows]);
  const totals = useMemo(() => sumTotals(visibleRows), [visibleRows]);
  const categoryOptions = useMemo(() => getCategoryOptions(optionRows), [optionRows]);
  const typeOptions = useMemo(() => getTypeOptions(optionRows, filters.categories), [optionRows, filters.categories]);

  const { showRefresh, setShowRefresh, refreshing, lastRefreshed, confirmRefresh } = useRefreshFlow({
    appliedFilters,
    loadReport,
  });

  if (!canView) return <Navigate to="/reports" replace />;

  const handleFilterChange = (field, value) => {
    setFilters((previous) => pruneSubTypes(applyFilterChange(previous, field, value), optionRows));
    setFilterError("");
  };

  const handleApply = () => {
    const message = validateFilters(filters);
    setFilterError(message);
    if (!message) void loadReport(filters);
  };

  const handleClear = () => {
    const defaults = getDefaultFilters();
    setFilters(defaults);
    setFilterError("");
    setCollapsedKeys(new Set());
    void loadReport(defaults);
  };

  const handleToggleMonth = (key) => setCollapsedKeys((previous) => toggleKey(previous, key));

  const handleToggleAll = () => {
    setCollapsedKeys(collapsedKeys.size ? new Set() : new Set(groups.map((group) => group.key)));
  };

  const handleExport = () => {
    if (!groups.length) {
      toast.info("There is nothing to export for the selected filters.");
      return;
    }
    downloadCsv(buildCsvFilename(appliedFilters), buildCsv(groups, totals));
  };

  const handleReturnsClosed = () => {
    setSelectedRow(null);
    void loadReport(appliedFilters);
  };

  return (
    <main className="pnl-report">
      <div className="main__container">
        <div className="main__tile_heading cus-btn-include-sec zIndex3">
          <span>Profit &amp; Loss Summary</span>
          <div className="top_right_button_container">
            <div className="pnl-last-refreshed">
              Last refreshed<br />
              <strong>{formatLastRefreshed(lastRefreshed)}</strong>
            </div>
            <button type="button" className="btn cus-seconday-bg-btn mr-2" onClick={() => setShowRefresh(true)} disabled={loading}>
              Refresh
            </button>
            <button type="button" className="btn cus-seconday-bg-btn mr-2" onClick={handleExport} disabled={loading}>
              Export CSV
            </button>
            <button type="button" className="btn cus-primary-transparent-btn" onClick={() => navigate("/reports")}>
              Back
            </button>
          </div>
        </div>
        <p className="pnl-subtitle">Monthly gross and net by category, read from stored report snapshots.</p>

        <PnLFilters
          filters={filters}
          categoryOptions={categoryOptions}
          typeOptions={typeOptions}
          companyOptions={companyOptions}
          sellerOptions={sellerOptions}
          warehouseOptions={warehouseOptions}
          errorMessage={filterError}
          disabled={loading}
          onChange={handleFilterChange}
          onApply={handleApply}
          onClear={handleClear}
        />
        <PnLKpis totals={totals} />
        <PnLLegend allCollapsed={collapsedKeys.size > 0} disabled={!groups.length} onToggleAll={handleToggleAll} />
        <PnLResults
          loading={loading}
          loadError={loadError}
          groups={groups}
          totals={totals}
          collapsedKeys={collapsedKeys}
          onToggleMonth={handleToggleMonth}
          onSelectRow={setSelectedRow}
        />
      </div>

      {selectedRow && (
        <PnLDetailDrawer
          row={selectedRow}
          filters={appliedFilters}
          onClose={() => setSelectedRow(null)}
          onReturnsClosed={handleReturnsClosed}
        />
      )}
      <PnLRefreshModal
        show={showRefresh}
        refreshing={refreshing}
        onCancel={() => setShowRefresh(false)}
        onConfirm={confirmRefresh}
      />
    </main>
  );
};

export default PnLSummaryReport;
